import { useForm } from "react-hook-form";
import { DESCRIPTION, ENTITY_ID, ENTITY_TYPE, FILE, UPLOADED_FILES } from "./consts";
import { schema } from "./validations";
import { attachmentEntityTypes, getRequiredFields, removeEmptyFields, setEmptyFieldsToNull } from "@/lib/utils";
import { useParams } from "react-router-dom";
import { routes } from "@/routes/utils";
import { useEffect, useRef, useState } from "react";
import { useAddAttachmentWithEntityMutation, useDeleteAttachmentMutation, useGetAttachmentWithEntity, useGetPresignedUrl } from "@/services";
import { toast } from "@/components";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";

const useProjectDesignDoc = () => {
  const { id, action } = useParams();
  const isView = action == routes.view;
  const attachmentRef = useRef<any>(null);
  const queryClient = useQueryClient();
  const [selectedImageId, setSelectedImageId] = useState<string>("");
  const [showImageDialog, setShowImageDialog] = useState<boolean>(false);

  const form = useForm<any>({
    defaultValues: {
      [ENTITY_ID]: id,
      [ENTITY_TYPE]: attachmentEntityTypes.projectDesignDoc,
      [FILE]: null,  
      [DESCRIPTION]: "",
      [UPLOADED_FILES]: []
    },
  });

  const requiredFields = getRequiredFields(schema);

  const {data: attachmentInfo, isLoading: isAttachmentLoading} = useGetAttachmentWithEntity({
    entityId: id as string,
    entityType: attachmentEntityTypes.projectDesignDoc,
  });

  const {data: imageData, isLoading: isImageLoading, isRefetching: isImageRefetching, refetch: imageRefetch} = useGetPresignedUrl({
    fileUploadId: selectedImageId as string,
    queryParams: {
      enabled: false
    }
  })

  useEffect(() => {
    if (attachmentInfo?.data) {
      form.setValue(UPLOADED_FILES, [
        ...attachmentInfo?.data
      ])
    }
  }, [attachmentInfo]);

  const clearFile = () => {
    form.reset({
      ...form.getValues(),
      [ENTITY_ID]: id,
      [ENTITY_TYPE]: attachmentEntityTypes.projectDesignDoc,
      [FILE]: null,  
      [DESCRIPTION]: "",
    })
  };

  const { mutate: addAttachmentInfo, isPending: isAddAttachmentPending } = useAddAttachmentWithEntityMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      clearFile();
      toast({ message: "File Uploaded Successfully" });
      queryClient.invalidateQueries({queryKey: ["attachment", form.getValues()[ENTITY_ID], form.getValues()[ENTITY_TYPE]]})
      // navigate(
      //   `/${routes.pm}/${routes.projectOverview}/${id}/${routes.edit}/${routes.projectPlan}`
      // );
    },
  });

  const { mutate: deleteAttachmentInfo, isPending: isDeleteAttachmentPending } = useDeleteAttachmentMutation({
    onError: (error?: any) => {
      toast({
        message:
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong",
        type: "error",
      });
      return error?.response?.data?.message;
    },
    onSuccess: () => {
      toast({ message: "File Deleted Successfully" });
      queryClient.invalidateQueries({queryKey: ["attachment", form.getValues()[ENTITY_ID], form.getValues()[ENTITY_TYPE]]})
    },
  });

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(setEmptyFieldsToNull(data));

    if (!_data[FILE]) {
      form.setError(FILE, {message: 'This field is required'});
      return;
    }
    
    delete _data[UPLOADED_FILES]
    addAttachmentInfo({
      data: {..._data}
    })
  }

  const onViewImage = (id: string) => {
    setSelectedImageId(id);

    setTimeout(() => {
      imageRefetch()
      setShowImageDialog(true)
    }, 100)
  }

  const onDeleteAttachment = (attachmentId: string) => () => {
    if (isDeleteAttachmentPending) return;
    deleteAttachmentInfo({attachmentId})
  }

  // Define Table Columns
  const columns = [
    {
      accessorKey: "fileName",
      header: "Attachment",
      cell: ({ row }: any) => {
        return row.original.fileName ? (
            <span
              className="text-secondary border-b-1 border-secondary group-hover:border-secondary cursor-pointer font-[500]"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onViewImage(row?.original?.fileUploadId)
              }}
            >
              {String(row.original.fileName)}
            </span>
        ) : (
          <div className="px-3">{""}</div>
        );
      },
      size: 200,
    },
    {
      accessorKey: "fileDescription",
      header: <div className="w-full">Description</div>,
      accessorFn: (row: any) => String(row?.fileDescription || ""),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <Trash2 className="h-5 w-5 stroke-red-600 cursor-pointer" onClick={onDeleteAttachment(row?.row?.original?.fileUploadId)} />  
      ),
    },
  ];
  
  return {
    form,
    requiredFields,
    isView,
    attachmentRef,
    onSubmit,
    isAddAttachmentPending,
    isAttachmentLoading,
    onViewImage,
    showImageDialog,
    setShowImageDialog,
    onDeleteAttachment,
    isDeleteAttachmentPending,
    imageData,
    isImageLoading,
    isImageRefetching,
    columns
  }
}

export default useProjectDesignDoc;